import React, { FC } from 'react';
import { Alert, Tag, Tooltip } from 'antd';
import {
	CheckCircleOutlined,
	CloseCircleOutlined,
	ExclamationCircleOutlined,
} from '@ant-design/icons';
import {
	CHECKLIST_CONFIDENCE_LEVELS,
	CHECKLIST_DOCUMENT_TYPE_DISPLAY_NAMES,
	CHECKLIST_ITEM_SEVERITIES,
	CHECKLIST_RESULT_STATUSES,
	ChecklistEvaluation,
	ChecklistExtractionItem,
} from '../../types/ComplianceChecklistTypes';
import {
	CHECKLIST_RESULT_STATUS_COLORS,
	CHECKLIST_RESULT_STATUS_DISPLAY_NAMES,
	EVALUATION_OVERALL_STATUS_ALERT_TYPES,
	EVALUATION_OVERALL_STATUS_DISPLAY_NAMES,
} from './ComplianceConstants';
import {
	EnrichedEvaluationResult,
	buildSkippedResultRows,
	deriveOverallEvaluationStatus,
	describeDocumentIssue,
	describeFoundValue,
	groupEvaluationResultsBySection,
	joinResultsToChecklistItems,
	resultNeedsHumanReview,
	summarizeEvaluationResults,
} from '../../utils/ComplianceEvaluationUtils';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { describeChecklistRequirement } from './ComplianceChecklistReview';

const RESULT_STATUS_ICONS = {
	[CHECKLIST_RESULT_STATUSES.SATISFIED]: CheckCircleOutlined,
	[CHECKLIST_RESULT_STATUSES.NOT_SATISFIED]: CloseCircleOutlined,
	[CHECKLIST_RESULT_STATUSES.NEEDS_HUMAN_REVIEW]: ExclamationCircleOutlined,
};

interface ComplianceEvaluationResultsProps {
	evaluation: ChecklistEvaluation;
	// Items from the request's stored checklistExtraction. They carry the section,
	// label and severity the results themselves do not; without them the view
	// degrades to a flat list keyed off item_key.
	checklistItems: ChecklistExtractionItem[];
	// Name of the document that was checked.
	fileName?: string;
}

// Read-only view of an AI evaluation of a supplier's document, grouped by the
// section of the source form with the requirements that need action first.
// Written for the supplier and their broker: no item keys, no check-type names.
export const ComplianceEvaluationResults: FC<ComplianceEvaluationResultsProps> = ({
	evaluation,
	checklistItems,
	fileName,
}): React.ReactElement => {
	const overallStatus = deriveOverallEvaluationStatus(evaluation, checklistItems);
	const enrichedResults = joinResultsToChecklistItems(evaluation, checklistItems);
	// A checklist item the model never answered still gets a row. The counts below
	// and the overall verdict both already account for it, so leaving it out would
	// promise a requirement to check that the sections never name.
	const sections = groupEvaluationResultsBySection(
		enrichedResults.concat(buildSkippedResultRows(evaluation, checklistItems))
	);
	// Null when there is no issue; an issue we do not recognise still gets a line.
	const documentIssueMessage = describeDocumentIssue(nullSafeGet('document_issue', evaluation));
	const detectedType = nullSafeGet('document_type_detected', evaluation);
	const unmappedObservations = nullSafeGetOrElse('unmapped_observations', evaluation, []);
	// Shared with the compact row summaries so the tallies can never disagree.
	const counts = summarizeEvaluationResults(evaluation, checklistItems);

	const renderResultTags = (result: EnrichedEvaluationResult) => {
		const isRecommended = result.severity === CHECKLIST_ITEM_SEVERITIES.RECOMMENDED;
		const isLowConfidence = result.confidence === CHECKLIST_CONFIDENCE_LEVELS.LOW;
		return (
			<div className="flex shrink-0 flex-row items-center gap-1">
				{isRecommended ? <Tag color="blue">Recommended</Tag> : null}
				{isLowConfidence ? (
					<Tooltip title="We had trouble reading this value on your document.">
						<Tag color="gold">Hard to read</Tag>
					</Tooltip>
				) : null}
			</div>
		);
	};

	const renderResult = (result: EnrichedEvaluationResult) => {
		// Show an unrecognised status as "needs a closer look" so the row agrees with
		// the overall verdict instead of falling through to an unstyled raw string.
		const displayStatus = resultNeedsHumanReview(result.status)
			? CHECKLIST_RESULT_STATUSES.NEEDS_HUMAN_REVIEW
			: result.status;
		const IconComponent = RESULT_STATUS_ICONS[displayStatus];
		const statusColor = CHECKLIST_RESULT_STATUS_COLORS[displayStatus];
		const isSatisfied = result.status === CHECKLIST_RESULT_STATUSES.SATISFIED;
		const location = nullSafeGet('location', result);
		// A skipped item carries no model output at all, so both of its lines are
		// ours: saying "found: blank" would report the document as missing a value
		// the model never went looking for.
		const foundClause = result.skipped
			? ' — not checked on your document'
			: ` — found: ${describeFoundValue(result)}`;
		const supplierMessage = result.skipped
			? 'We could not check this requirement, so someone will review it by hand.'
			: isSatisfied
			? null
			: nullSafeGet('supplier_message', result);
		return (
			<div
				key={result.item_key}
				className="flex flex-row items-start gap-3 border-0 border-b border-solid border-grey-4 py-3 last:border-b-0"
			>
				<span className="mt-1 shrink-0" style={{ color: statusColor }}>
					{IconComponent ? <IconComponent /> : null}
				</span>
				<div className="flex min-w-0 flex-1 flex-col gap-1">
					<div className="flex flex-row items-start justify-between gap-4">
						<span className="bodymedium">{result.label}</span>
						{renderResultTags(result)}
					</div>
					<span className="footnoteregular text-character-secondary">
						<span style={{ color: statusColor }}>
							{CHECKLIST_RESULT_STATUS_DISPLAY_NAMES[displayStatus]}
						</span>
						{foundClause}
						{result.item ? `; required: ${describeChecklistRequirement(result.item)}` : ''}
					</span>
					{supplierMessage ? <span className="footnoteregular">{supplierMessage}</span> : null}
					{location ? (
						<span className="footnoteregular text-grey-7">On your document: {location}</span>
					) : null}
				</div>
			</div>
		);
	};

	const renderSection = (section) => {
		const sectionNotMet = section.results.filter(
			(result) => result.status !== CHECKLIST_RESULT_STATUSES.SATISFIED
		).length;
		return (
			<div key={section.name} className="flex flex-col">
				<div className="flex flex-row items-center justify-between gap-2 bg-grey-3 px-4 py-2">
					<span className="bodybold">{section.name}</span>
					<span className="footnoteregular text-character-secondary">
						{sectionNotMet > 0
							? `${sectionNotMet} of ${section.results.length} need attention`
							: 'All met'}
					</span>
				</div>
				<div className="px-4">{section.results.map(renderResult)}</div>
			</div>
		);
	};

	return (
		<div className="flex flex-col gap-4">
			<Alert
				type={EVALUATION_OVERALL_STATUS_ALERT_TYPES[overallStatus] || 'info'}
				showIcon={true}
				message={EVALUATION_OVERALL_STATUS_DISPLAY_NAMES[overallStatus] || 'Checked'}
				description={nullSafeGet('summary_for_supplier', evaluation)}
			/>
			{documentIssueMessage ? (
				<Alert
					type="warning"
					showIcon={true}
					message={documentIssueMessage}
					description={nullSafeGet('document_issue_detail', evaluation)}
				/>
			) : null}
			{counts.total > 0 ? (
				<div className="footnoteregular flex flex-row flex-wrap gap-x-6 gap-y-1 text-character-secondary">
					<span>
						{counts.met} of {counts.total} met
					</span>
					{counts.notMet > 0 ? <span>{counts.notMet} not met</span> : null}
					{counts.needsReview > 0 ? <span>{counts.needsReview} need a closer look</span> : null}
					{detectedType ? (
						<span>
							We read this as: {CHECKLIST_DOCUMENT_TYPE_DISPLAY_NAMES[detectedType] || detectedType}
						</span>
					) : null}
					{fileName ? <span>{fileName}</span> : null}
				</div>
			) : null}
			{sections.length > 0 ? (
				<div className="flex flex-col overflow-hidden rounded border border-solid border-grey-4">
					{sections.map(renderSection)}
				</div>
			) : null}
			{unmappedObservations.length > 0 ? (
				<div className="flex flex-col gap-1">
					<span className="h5medium">Also worth knowing</span>
					<div className="rounded border border-solid border-grey-4 px-4 py-2">
						{unmappedObservations.map((observation, index) => (
							<div key={index} className="footnoteregular py-1 text-character-secondary">
								{observation}
							</div>
						))}
					</div>
				</div>
			) : null}
		</div>
	);
};
